import { AnyError } from '@if/mui'
import { apiClient } from '@src/services/http-common'
import { ESystemMatchingResult, InteracPossibleMatch, InteracWire, InteracWireFiltersType } from '@src/types/interac'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

async function getInteracWireByFilters(filters: InteracWireFiltersType) {
  const response = await apiClient.post<InteracWire[]>('/GetInteracWireByFilters', filters)
  return response.data ?? []
}

export function useInteracWireByFilters(filters: InteracWireFiltersType): [InteracWire[], boolean, Error | null] {
  const { data, isFetching, error } = useQuery({
    queryKey: ['interacWire', filters],
    queryFn: () => getInteracWireByFilters(filters),
    enabled: !!filters,
  })
  return [data ?? [], isFetching, error]
}

export interface DeleteInteracWireParams {
  id: string
  versionTag: string
  reason: string
}

async function deleteInteracWire(params: DeleteInteracWireParams) {
  const { data } = await apiClient.put<InteracWire>('/DiscardInteracWire', params)
  return data
}

export function useDeleteInteracWire() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteInteracWire,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        predicate: (query) => {
          const [key, filters] = query.queryKey as [string, InteracWireFiltersType]
          return (
            key === 'interacWire' &&
            filters?.isUnmatchedWire === true &&
            // Only Sure Match wires can't be deleted
            filters?.systemMatchingResult !== ESystemMatchingResult.Sure
          )
        },
      })
    },
  })
}

export interface MatchInteracWireParams {
  id: string
  versionTag: string
  loanId: string
  dateOverride: string | null
}

async function matchInteracWire(params: MatchInteracWireParams) {
  const { data } = await apiClient.post<InteracWire>('/RecordInteracWirePayment', params)
  return data
}

export function useMatchInteracWire() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: matchInteracWire,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        predicate: (query) => {
          const [key, filters] = query.queryKey as [string, InteracWireFiltersType]
          // Invalidate only the unmatched wires query
          return key === 'interacWire' && filters?.isUnmatchedWire === true
        },
      })
    },
  })
}

export interface InteracWireSearchPossibleMatchFilters {
  referenceNumber: string | null
  email: string | null
}

async function searchPossibleMatch(id: string, filters: InteracWireSearchPossibleMatchFilters) {
  const { data } = await apiClient.post<InteracPossibleMatch[]>(`/InteracWire/${id}/SearchPossibleMatch`, filters)
  return data
}

export function useSearchPossibleMatch(
  id: string | null,
  filters: InteracWireSearchPossibleMatchFilters | null,
): [InteracPossibleMatch[], boolean, AnyError | null] {
  const { data, isFetching, error } = useQuery({
    queryKey: ['interacWire', id, 'searchPossibleMatch', filters],
    queryFn: () => searchPossibleMatch(id!, filters!),
    enabled: Boolean(id && filters),
  })
  return [data ?? [], isFetching, error]
}

async function resetPossibleMatch(id: string) {
  const { data } = await apiClient.put<InteracWire>(`/InteracWire/${id}/ResetPossibleMatch`)
  return data
}

export function useResetInteracPossibleMatch() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: resetPossibleMatch,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        predicate: (query) => {
          const [key, filters] = query.queryKey as [string, InteracWireFiltersType]
          return key === 'interacWire' && filters?.isUnmatchedWire === true
        },
      })
    },
  })
}
